import { attr } from '@ember-data/model';
import { buildValidations, validator } from 'ember-cp-validations';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { fragmentOwner } from 'ember-data-model-fragments/attributes';
import { relationshipName } from 'client-portal/utils/relationship-types';
import Fragment from 'ember-data-model-fragments/fragment';

function presenceDisabledWhenNotRequired(requiredGetter) {
  return computed(`model.${requiredGetter}`, function () {
    return !this.get(`model.${requiredGetter}`);
  });
}

const Validations = buildValidations({
  firstName: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.contactInfoContact.firstName',
      messageKey: 'errors.presence',
    }),
  ],
  lastName: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.contactInfoContact.lastName',
      messageKey: 'errors.presence',
    }),
  ],
  emailAddress: [
    validator('format', {
      type: 'email',
      allowBlank: true,
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactEmailAddress',
      messageKey: 'errors.format.email',
    }),
    validator('presence', {
      presence: true,
      disabled: presenceDisabledWhenNotRequired('isEmailRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactEmailAddress',
      messageKey: 'errors.presence',
    }),
  ],
  phoneNumber: [
    validator('presence', {
      presence: true,
      disabled: presenceDisabledWhenNotRequired('isPhoneRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactPhoneNumber',
      messageKey: 'errors.presence',
    }),
    validator('length', {
      allowBlank: true,
      min: 10,
      max: 24,
      messageKey: 'errors.phone.invalid_length',
    }),
    validator('format', {
      allowBlank: true,
      regex: /^[0-9 .\-()x]+$/,
      messageKey: 'errors.phone.invalid_format',
    }),
  ],
  relationshipName: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactType',
      messageKey: 'errors.presence',
    }),
  ],
});

export default class ContactInfoContact extends Fragment.extend(Validations) {
  @fragmentOwner() demographicsForm;

  @attr('number') relationshipId;
  @attr('string') firstName;
  @attr('string') lastName;
  @attr('string') relationshipName;
  @attr('boolean') emergencyContact;
  @attr('string') phoneNumber;
  @attr('string', { defaultValue: 'Mobile' }) phoneType;
  @attr('boolean', { defaultValue: false }) phoneLeaveVoiceMessage;
  @attr('boolean', { defaultValue: false }) phoneLeaveTextMessage;
  @attr('number', { defaultValue: 0 }) enableSmsvoiceReminders;
  @attr('string') emailAddress;
  @attr('string', { defaultValue: 'Home' }) emailType;
  @attr('boolean', { defaultValue: false }) emailConsentGiven;
  @attr('boolean', { defaultValue: false }) enableEmailReminders;
  @attr('boolean') isRecipient;
  @attr('boolean') isMarkedForDeletion;

  get isNewContact() {
    return this.relationshipId === null || this.relationshipId === undefined;
  }

  get isOldContact() {
    return !this.isNewContact;
  }

  get fullName() {
    return [this.firstName, this.lastName].filter(Boolean).join(' ').trim();
  }

  @dependentKeyCompat
  get emergencyContactRequiredFields() {
    const fields = this.demographicsForm?.demographicFormRequiredFields ?? [];
    return fields.filter(field => field.startsWith('emergency_contact'));
  }

  @dependentKeyCompat
  get isRequiredEmergencyContactEmailAddress() {
    return this.emergencyContactRequiredFields.includes('emergency_contact_email_address');
  }

  @dependentKeyCompat
  get isRequiredEmergencyContactPhoneNumber() {
    return this.emergencyContactRequiredFields.includes('emergency_contact_phone_number');
  }

  @dependentKeyCompat
  get isEmailRequired() {
    return (
      this.isNewContact && this.emergencyContact && this.isRequiredEmergencyContactEmailAddress
    );
  }

  @dependentKeyCompat
  get isPhoneRequired() {
    return this.isNewContact && this.emergencyContact && this.isRequiredEmergencyContactPhoneNumber;
  }

  get missingRequiredEmailForEmergencyContact() {
    return this.isRequiredEmergencyContactEmailAddress && !this.emailAddress?.trim();
  }

  get missingRequiredPhoneForEmergencyContact() {
    return this.isRequiredEmergencyContactPhoneNumber && !this.phoneNumber?.trim();
  }

  get isEmergencySelectionBlocked() {
    return this.isNewContact && this.hasMissingEmergencyContactData;
  }

  get hasMissingEmergencyContactData() {
    return (
      this.missingRequiredEmailForEmergencyContact || this.missingRequiredPhoneForEmergencyContact
    );
  }

  get emergencySelectionTooltipText() {
    let tooltipKey = '';

    if (
      this.missingRequiredEmailForEmergencyContact &&
      this.missingRequiredPhoneForEmergencyContact
    ) {
      tooltipKey = 'site.documents.contactInfo.contactsListSection.tooltips.missingBoth';
    } else if (this.missingRequiredEmailForEmergencyContact) {
      tooltipKey = 'site.documents.contactInfo.contactsListSection.tooltips.missingEmail';
    } else if (this.missingRequiredPhoneForEmergencyContact) {
      tooltipKey = 'site.documents.contactInfo.contactsListSection.tooltips.missingPhone';
    }

    return tooltipKey;
  }

  get relationshipDisplayName() {
    return relationshipName(this.relationshipName);
  }

  get contactSummary() {
    return [this.relationshipDisplayName, this.emailAddress, this.phoneNumber]
      .filter(Boolean)
      .join(' • ');
  }
}
